import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import Table from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import { generatePdfReport } from "../../../../utils/pdf/generatePdfReport";
import numeroParaExtenso from "../../../../utils/numeroPorExtenso";

/** ---------------------- Tipos ---------------------- */

type RetornosConfig = {
  areaSlug: string;
  estoqueSlug: string;
  key: string;

  departamento: string;
  awaitDepartamento: string;

  title: string;
  reportTitle: string;
  fileNamePrefix: string;
};

export type RetornoItemAPI = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  produto: {
    id: number;
    codigo: string;
    nome: string;
    posicao: string | null;
  };
};

export type RetornoMovAPI = {
  movimentacao: {
    id: number;
    responsavel_id: number | null;
    responsavel_nome: string | null;
    departamento_origem: string | null;
    os_numero?: string | null;
    pv_numero?: string | null;
    proposta_venda?: string | null;
    criado_em: string;
  };
  itens: RetornoItemAPI[];
};

type RespAPI = {
  ok: boolean;
  meta: {
    filtros: {
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC";
      limit: number;
      offset: number;
    };
    count: number;
  };
  data: RetornoMovAPI[];
};

export type RetornoRowUI = [
  string, // Responsável Pelo Retorno
  string, // PV/OS
  string, // Posição
  string, // Código
  string, // Nome do Produto
  string, // Quantidade
  string, // Data
  string // Hora
];

export type RetornosQuery = Partial<{
  date_from: string;
  date_to: string;
  order: "asc" | "desc";
  limit: number;
  offset: number;
}>;

type AwaitResult = {
  changed: boolean;
  versao: number | null;
};

/** ---------------------- Constantes ---------------------- */

const API_BASE = "https://api.eva-googlemarine.com";

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

const ACCESS_TOKEN_KEY = "access_token";

/** ---------------------- Configs antigas + fallback dinâmico ---------------------- */

const RETORNOS_CONFIGS: RetornosConfig[] = [
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-servico-e-comercial",
    key: "suprimentos-servico-comercial",
    departamento: "suprimentos",
    awaitDepartamento: "suprimentos",
    title: "Suprimentos - Retornos no Estoque de Serviço & Comercial",
    reportTitle: "Relatório de Retornos Estoque - Suprimentos",
    fileNamePrefix: "Suprimentos-Estoque-Retornos",
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-servico-campos",
    key: "suprimentos-campos",
    departamento: "suprimentos-campos",
    awaitDepartamento: "suprimentos-campos",
    title: "Suprimentos - Retornos no Estoque p/ Serviço Campos",
    reportTitle: "Relatório de Retornos Estoque - Suprimentos Campos",
    fileNamePrefix: "Suprimentos-Campos-Estoque-Retornos",
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-tecnicos-e-laboratorio",
    key: "tecnicos",
    departamento: "tecnicos",
    awaitDepartamento: "tecnicos",
    title: "Laboratório - Retornos no Estoque",
    reportTitle: "Relatório de Retornos Estoque - Laboratório",
    fileNamePrefix: "Laboratorio-Estoque-Retornos",
  },
];

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function slugToTitle(slug: string): string {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part === "p") return "p/";
      if (part === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function getConfigByParams(
  areaSlug?: string,
  estoqueSlug?: string
): RetornosConfig {
  const area = normalizeSlug(areaSlug);
  const estoque = normalizeSlug(estoqueSlug);

  const found = RETORNOS_CONFIGS.find(
    (config) => config.areaSlug === area && config.estoqueSlug === estoque
  );

  if (found) return found;

  const safeArea = area || "suprimentos";
  const safeEstoque = estoque || "estoque";

  const estoqueTitle = slugToTitle(safeEstoque);

  return {
    areaSlug: safeArea,
    estoqueSlug: safeEstoque,
    key: `${safeArea}-${safeEstoque}`,
    departamento: safeEstoque,
    awaitDepartamento: safeEstoque,
    title: `${slugToTitle(safeArea)} - Retornos - ${estoqueTitle}`,
    reportTitle: `Relatório de Retornos Estoque - ${estoqueTitle}`,
    fileNamePrefix: `${safeArea}-${safeEstoque}-Retornos`,
  };
}

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  const m = iso.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z$/
  );

  if (!m) {
    return {
      data: iso,
      hora: "",
    };
  }

  const [, ano, mes, dia, hora, minuto] = m;

  return {
    data: `${dia}/${mes}/${ano}`,
    hora: `${hora}:${minuto}`,
  };
}

function parseQuantidade(value: unknown): number {
  const n = parseInt(String(value ?? "0"), 10);
  return Number.isFinite(n) ? n : 0;
}

function parseBRLCurrency(value: unknown): number {
  const raw = String(value ?? "")
    .replace(/[^\d,.-]/g, "")
    .trim();

  if (!raw) return 0;

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");

  let normalized = raw;

  if (lastComma > lastDot) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma) {
    normalized = raw.replace(/,/g, "");
  }

  const n = Number(normalized);

  return Number.isFinite(n) ? n : 0;
}

function formatBRL(valor: number): string {
  return valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getPvOs(mov: RetornoMovAPI["movimentacao"]): string {
  const value = mov.os_numero ?? mov.pv_numero ?? mov.proposta_venda ?? null;

  const clean = String(value ?? "").trim();

  return clean || "-";
}

function flattenToRows(movs: RetornoMovAPI[]): RetornoRowUI[] {
  const rows: RetornoRowUI[] = [];

  for (const mov of movs || []) {
    const { data, hora } = toBRT(mov.movimentacao?.criado_em || "");
    const responsavel = mov.movimentacao?.responsavel_nome || "-";
    const pvOs = getPvOs(mov.movimentacao);

    for (const item of mov.itens || []) {
      const quantidade = Number(item.quantidade || 0);

      rows.push([
        responsavel,
        pvOs,
        item.produto?.posicao ?? "-",
        String(item.produto?.codigo ?? "").toUpperCase(),
        String(item.produto?.nome ?? "").toUpperCase(),
        String(Number.isFinite(quantidade) ? quantidade : 0),
        data,
        hora,
      ]);
    }
  }

  return rows;
}

function buildQuery(config: RetornosConfig, query?: RetornosQuery): string {
  const params = new URLSearchParams();

  params.set("estoque_slug", config.estoqueSlug);

  if (query?.date_from) params.set("date_from", query.date_from);
  if (query?.date_to) params.set("date_to", query.date_to);
  if (query?.order) params.set("order", query.order);
  if (typeof query?.limit === "number")
    params.set("limit", String(query.limit));
  if (typeof query?.offset === "number")
    params.set("offset", String(query.offset));

  const s = params.toString();

  return s ? `?${s}` : "";
}

function getRetornosUrl(config: RetornosConfig): string {
  return `${API_BASE}/departamentos/${encodeURIComponent(
    config.departamento
  )}/retornos`;
}

function getAwaitUrl(config: RetornosConfig, since: number): string {
  const params = new URLSearchParams();

  params.set("since", String(since));
  params.set("timeout_ms", String(AWAIT_TIMEOUT_MS));
  params.set("estoque_slug", config.estoqueSlug);

  return `${API_BASE}/departamentos/${encodeURIComponent(
    config.awaitDepartamento
  )}/retornos/await?${params.toString()}`;
}

/** ---------------------- Fetchers ---------------------- */

async function fetchRetornosOnce(
  config: RetornosConfig,
  query?: RetornosQuery
): Promise<{ rows: RetornoRowUI[]; meta: RespAPI["meta"] | null }> {
  const res = await fetch(
    `${getRetornosUrl(config)}${buildQuery(config, query)}`,
    {
      method: "GET",
      headers: buildAuthHeaders(),
      cache: "no-store",
    }
  );

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }

  const json = (await res.json()) as RespAPI;

  if (!json.ok) {
    return {
      rows: [],
      meta: json.meta ?? null,
    };
  }

  return {
    rows: flattenToRows(Array.isArray(json.data) ? json.data : []),
    meta: json.meta ?? null,
  };
}

async function awaitChange(
  config: RetornosConfig,
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const res = await fetch(getAwaitUrl(config, since), {
      method: "GET",
      headers: buildAuthHeaders(),
      signal,
    });

    if (!res.ok) return null;

    const json = await res.json();

    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;

    const changed = Boolean(json?.changed);

    return {
      changed,
      versao,
    };
  } catch {
    return null;
  }
}

/** ---------------------- Hook genérico ---------------------- */

function useRetornosGenerico(config: RetornosConfig, query?: RetornosQuery) {
  const [rows, setRows] = useState<RetornoRowUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  const versaoRef = useRef<number | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  async function carregar() {
    fetching.current = true;

    try {
      setErro(null);

      const result = await fetchRetornosOnce(config, query);

      setRows(result.rows);
      setMeta(result.meta);
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar retornos.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    versaoRef.current = null;
    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();

    setLoading(true);
    setErro(null);
    setRows([]);
    setMeta(null);

    (async () => {
      try {
        await carregar();
        setLoading(false);

        const initResp = await awaitChange(config, -1);

        if (initResp && typeof initResp.versao === "number") {
          versaoRef.current = initResp.versao;
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();

            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;

            try {
              resp = await awaitChange(config, cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                await sleep(jitter(ERROR_BACKOFF_MS));
              }
            } finally {
              if (awaitCtrlRef.current === ac) {
                awaitCtrlRef.current = null;
              }

              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null) {
                awaitSupported.current = false;
              }

              await sleep(POLL_FALLBACK_MS);
              continue;
            }

            if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);

            if (resp.changed || avancou) {
              versaoRef.current = resp.versao ?? cur;
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }

            continue;
          }

          await sleep(POLL_FALLBACK_MS);
          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useRetornosGenerico loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.key,
    config.areaSlug,
    config.estoqueSlug,
    config.departamento,
    JSON.stringify(query),
  ]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    rows,
    loading,
    refresh,
    meta,
    erro,
  };
}

/** ---------------------- Página ---------------------- */

const EstoqueRetornosGenerico: React.FC = () => {
  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug: string;
    estoqueSlug: string;
  }>();

  const config = useMemo(
    () => getConfigByParams(areaSlug, estoqueSlug),
    [areaSlug, estoqueSlug]
  );

  const headers = useMemo(
    () => [
      "Responsável Pelo Retorno",
      "PV/OS",
      "Posição",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Data",
      "Hora",
    ],
    []
  );

  const columnWidths = useMemo(
    () => ["150px", "60px", "10px", "10px", "300px", "60px", "70px", "10px"],
    []
  );

  const enabledColumnIndexes = useMemo(
    () => headers.map((_header, index) => index),
    [headers]
  );

  const { rows, loading, erro } = useRetornosGenerico(config);

  const totalProdutos = useMemo(() => {
    return rows.reduce((acc, row) => {
      const quantidade = parseQuantidade(row[5]);
      return acc + quantidade;
    }, 0);
  }, [rows]);

  /**
   * A tela antiga tentava calcular valor usando a coluna 4,
   * mas essa coluna é "Nome do Produto", não custo.
   * Mantive valorTotal como 0 porque retornos não exibem custo unitário.
   */
  const valorTotal = useMemo(() => {
    return rows.reduce((acc, row) => {
      const custo = parseBRLCurrency("");
      const quantidade = parseQuantidade(row[5]);

      const incremento =
        Number.isFinite(custo) && Number.isFinite(quantidade)
          ? custo * quantidade
          : 0;

      return acc + incremento;
    }, 0);
  }, [rows]);

  const gerarRelatorioRetornos = useCallback(() => {
    generatePdfReport({
      reportTitle: config.reportTitle,
      fileNamePrefix: config.fileNamePrefix,
      subtitle: "Informações dos Produtos Retornados ao Estoque",
      headers,
      rows,
      custoColIndex: -1,
      enabledColumns: enabledColumnIndexes,
      footerLines: [
        `Total de Produtos Retornados ao Estoque: ${totalProdutos}`,
        `Valor Total dos Produtos Retornados ao Estoque: R$ ${valorTotal.toLocaleString(
          "pt-BR",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        )} (${numeroParaExtenso(Math.floor(valorTotal))} reais)`,
      ],
    });
  }, [
    config.reportTitle,
    config.fileNamePrefix,
    headers,
    rows,
    enabledColumnIndexes,
    totalProdutos,
    valorTotal,
  ]);

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{
        actionKey?: string;
        label?: string;
        codigo?: string | null;
        departamento?: {
          id?: number | string | null;
          name?: string;
          slug?: string | null;
          routeKey?: string;
        };
        modulo?: {
          id?: number | string | null;
          label?: string;
          codigo?: string | null;
          slug?: string;
        };
        pathContext?: {
          pathname?: string;
          selectedAddressValue?: string;
        };
      }>;

      if (custom.detail?.actionKey !== "generate_report") return;

      const currentPath = normalizeSlug(window.location.pathname);
      const eventPath = normalizeSlug(
        custom.detail?.pathContext?.pathname || ""
      );

      const moduloLabel = normalizeSlug(custom.detail?.modulo?.label || "");
      const moduloCodigo = normalizeSlug(custom.detail?.modulo?.codigo || "");
      const moduloSlug = normalizeSlug(custom.detail?.modulo?.slug || "");

      const baseDireto = normalizeSlug(
        `/${config.areaSlug}/${config.estoqueSlug}/retornos`
      );

      const baseLegadoEntradas = normalizeSlug(
        `/${config.areaSlug}/${config.estoqueSlug}/entradas/retornos`
      );

      const baseLegadoTecnicos = normalizeSlug(
        `/${config.areaSlug}/${config.estoqueSlug}/estoque/retornos`
      );

      const isRetornosContext =
        currentPath.includes(baseDireto) ||
        currentPath.includes(baseLegadoEntradas) ||
        currentPath.includes(baseLegadoTecnicos) ||
        eventPath.includes(baseDireto) ||
        eventPath.includes(baseLegadoEntradas) ||
        eventPath.includes(baseLegadoTecnicos) ||
        currentPath.includes("retornos") ||
        eventPath.includes("retornos") ||
        moduloLabel.includes("retornos") ||
        moduloCodigo.includes("retornos") ||
        moduloSlug.includes("retornos");

      if (!isRetornosContext) return;

      gerarRelatorioRetornos();
    };

    window.addEventListener("logvi:menu-action", handler);

    return () => {
      window.removeEventListener("logvi:menu-action", handler);
    };
  }, [config.areaSlug, config.estoqueSlug, gerarRelatorioRetornos]);

  return (
    <div>
      <TitleButtons fullBleedLine title={config.title} />

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {erro}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 16 }}>Carregando retornos…</div>
      ) : (
        <Table
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          responsiveWidths={{
            1300: {
              1: "90px",
              2: "80px",
              3: "10px",
              4: "0px",
              5: "300px",
              6: "0px",
              7: "140px",
              8: "0px",
            },
          }}
        />
      )}
    </div>
  );
};

export default EstoqueRetornosGenerico;
